import React, { useCallback, useEffect, useMemo, useState } from "react";
import { notification } from "antd";
import { MessageOutlined, SendOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTokens } from "../../../core/theme/TokensProvider";
import { Badge } from "../../../ui/primitives/Badge";
import { Button } from "../../../ui/primitives/Button";
import { Drawer } from "../../../ui/primitives/Drawer";
import { Input } from "../../../ui/primitives/Input";
import { Select } from "../../../ui/primitives/Select";
import { Stack } from "../../../ui/primitives/Stack";
import { Tag } from "../../../ui/primitives/Tag";
import { Text } from "../../../ui/primitives/Text";
import { useRetentionDashboardService } from "../../../services/members/RetentionDashboardServiceProvider";
import { useStaffUserService } from "../../../services/members/StaffUserServiceProvider";
import { useStaffIdentity } from "../../../common/staff/StaffIdentityProvider";
import { StaffMessage } from "../../../model/members/StaffMessage";
import { StaffUser } from "../../../model/members/StaffUser";

function formatDateTime(value: string): string {
  const d = dayjs(value);
  return d.isValid() ? d.format("DD MMM HH:mm") : value;
}

// FORT-369: the "All Reception Staff" option's value. Negative so it can never collide
// with a real staff_user id, and never sent to the server as one — the broadcast goes to
// its own endpoint with no recipient field, and the server resolves who they are.
const ALL_RECEPTION_STAFF = -1;

/**
 * Handover — internal messaging between managers and staff, reachable from anywhere.
 *
 * It lives in the header rather than on a page because it isn't tied to one screen: a
 * manager picks it up between other work, and reception staff need it on shift. It used
 * to be buried inside "My Dashboard", which the feedback removed.
 */
const HandoverPanel: React.FC = () => {
  const service = useRetentionDashboardService();
  const staffUserService = useStaffUserService();
  const { selectedStaffId } = useStaffIdentity();
  const tokens = useTokens();

  const [open, setOpen] = useState<boolean>(false);
  const [messages, setMessages] = useState<StaffMessage[]>([]);
  const [staffUsers, setStaffUsers] = useState<StaffUser[]>([]);
  const [recipientId, setRecipientId] = useState<number | undefined>(undefined);
  const [body, setBody] = useState<string>("");
  const [sending, setSending] = useState<boolean>(false);

  const load = useCallback(async () => {
    try {
      const [inbox, staff] = await Promise.all([
        service.getMyMessages(),
        staffUserService.getStaffUsers().catch(() => [] as StaffUser[]),
      ]);
      setMessages(inbox);
      setStaffUsers(staff);
    } catch {
      // An account with no linked staff profile has no inbox; the badge simply stays
      // empty rather than nagging on every page load.
      setMessages([]);
    }
  }, [service, staffUserService]);

  // Load once for the unread badge, and again whenever the panel is opened so a message
  // sent while it was closed shows up — or the staff picker changes who this device is
  // signed in as. The inbox is scoped to the resolved staff member, so after a switch the
  // badge would otherwise keep showing the previous person's unread count, sitting
  // directly beside the name that was just changed.
  useEffect(() => {
    load();
  }, [load, selectedStaffId]);

  const openPanel = useCallback(() => {
    setOpen(true);
    load();
  }, [load]);

  const unreadCount = useMemo(() => messages.filter((m) => !m.isRead).length, [messages]);

  // Only current staff — handing a shift over to someone who has left is meaningless.
  //
  // FORT-369: "All Reception Staff" heads the list, because a handover note is more often
  // for the desk than for one named person. It is a sentinel rather than a real staff id;
  // the server resolves the actual recipients, so this value is never sent as one.
  const staffOptions = useMemo(
    () => [
      { label: "All Reception Staff", value: ALL_RECEPTION_STAFF },
      ...staffUsers.filter((s) => s.isActive).map((s) => ({ label: s.name, value: s.id })),
    ],
    [staffUsers]
  );

  const markRead = useCallback(
    async (msg: StaffMessage) => {
      try {
        await service.markMessageRead(msg.id);
        setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, isRead: true } : m)));
      } catch {
        // The API client already surfaces the failure as a notification.
      }
    },
    [service]
  );

  const send = useCallback(async () => {
    // The Send button is disabled until both are set; this narrows the type.
    const text = body.trim();
    if (recipientId == null || !text) return;
    setSending(true);
    try {
      if (recipientId === ALL_RECEPTION_STAFF) {
        // Reported with the count: "sent" on a broadcast leaves the sender unable to tell
        // a full desk from an empty one, and the whole point is knowing everyone was told.
        const sent = await service.broadcastToAllStaff(text);
        // notification, not antd's static `message`: every other confirmation on this
        // screen comes through the API client's notification channel, and the static
        // helper can't consume the theme context so it wouldn't carry tenant branding.
        notification.success({
          message: "Success",
          description: `Message sent to ${sent.length} reception staff ${
            sent.length === 1 ? "member" : "members"
          }`,
        });
      } else {
        await service.sendMessage({ toStaffId: recipientId, body: text });
      }
      setBody("");
      setRecipientId(undefined);
    } catch {
      // The API client already surfaces the failure as a notification.
    } finally {
      setSending(false);
    }
  }, [service, recipientId, body]);

  return (
    <>
      <Badge count={unreadCount} data-testid="handover-unread-badge">
        <Button icon={<MessageOutlined />} data-testid="handover-open-btn" onClick={openPanel}>
          Handover
        </Button>
      </Badge>

      <Drawer
        title="Handover"
        open={open}
        onClose={() => setOpen(false)}
        width={460}
        data-testid="handover-drawer"
      >
        <Stack gap={6}>
          <Stack gap={3}>
            <Text variant="h5">Inbox ({unreadCount} unread)</Text>
            {messages.length === 0 ? (
              <Text color="secondary" data-testid="handover-empty">
                No messages yet.
              </Text>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  data-testid={`handover-message-${msg.id}`}
                  style={{
                    background: msg.isRead ? tokens.color.background.default : tokens.color.background.muted,
                    border: `1px solid ${tokens.color.border.default}`,
                    borderRadius: tokens.radius.md,
                    padding: tokens.space[3],
                  }}
                >
                  <Stack gap={1}>
                    <Stack direction="horizontal" justify="space-between" align="center" gap={2} wrap>
                      <Text weight="semibold">{msg.fromStaffName}</Text>
                      <Text variant="caption">{formatDateTime(msg.createdAt)}</Text>
                    </Stack>
                    <Text style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{msg.body}</Text>
                    {msg.isRead ? null : (
                      <Stack direction="horizontal" gap={2} align="center">
                        <Tag tone="primary">Unread</Tag>
                        <Button
                          variant="link"
                          size="sm"
                          data-testid={`handover-markread-${msg.id}`}
                          onClick={() => markRead(msg)}
                        >
                          Mark read
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                </div>
              ))
            )}
          </Stack>

          <Stack gap={2}>
            <Text variant="h5">Send a message</Text>
            <Select
              label="To"
              placeholder="Select staff member"
              options={staffOptions}
              value={recipientId}
              onChange={(value) => setRecipientId(value == null ? undefined : Number(value))}
              data-testid="handover-recipient"
            />
            <Input
              label="Message"
              multiline
              rows={3}
              value={body}
              placeholder="Type your message"
              data-testid="handover-body"
              onChange={(e) => setBody(e.target.value)}
            />
            <Button
              variant="primary"
              icon={<SendOutlined />}
              loading={sending}
              disabled={recipientId == null || !body.trim()}
              onClick={send}
              data-testid="handover-send"
            >
              Send
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    </>
  );
};

export default HandoverPanel;
